/**
 * Connected Products Web Component
 * Allows customers to switch between related products (different sizes, sections, upholstery, etc.)
 * without creating browser history entries - similar to variant selection behavior.
 */

if (!customElements.get('connected-products')) {
  customElements.define(
    'connected-products',
    class ConnectedProducts extends HTMLElement {
      constructor() {
        super();
        this.inputs = this.querySelectorAll('.connected-products__input');
        this.selectedDisplay = this.querySelector('.connected-products__selected span');
        this.sectionId = this.dataset.section;
        this.currentProductHandle = this.dataset.currentProductHandle;
        this.connectedProductsData = null;
      }

      connectedCallback() {
        this.parseConnectedProductsData();
        this.setupEventListeners();
      }

      parseConnectedProductsData() {
        const jsonScript = this.querySelector('[data-connected-products-json]');
        if (jsonScript) {
          try {
            this.connectedProductsData = JSON.parse(jsonScript.textContent);
          } catch (e) {
            console.error('Connected Products: Error parsing JSON data', e);
          }
        }
      }

      setupEventListeners() {
        this.boundOnProductChange = this.onProductChange.bind(this);
        this.inputs.forEach((input) => {
          input.addEventListener('change', this.boundOnProductChange);
        });
      }

      onProductChange(event) {
        const input = event.target;
        const productUrl = input.dataset.productUrl;
        const productTitle = input.dataset.productTitle;
        const productHandle = input.value;

        if (productHandle === this.currentProductHandle) {
          return;
        }

        if (this.selectedDisplay) {
          this.selectedDisplay.textContent = productTitle;
        }

        this.classList.add('connected-products--loading');
        this.navigateToProduct(productUrl, productHandle);
      }

      navigateToProduct(productUrl, productHandle) {
        window.history.replaceState(
          { connectedProduct: productHandle },
          '',
          productUrl
        );
        this.fetchAndUpdatePage(productUrl);
      }

      async fetchAndUpdatePage(productUrl) {
        try {
          const response = await fetch(productUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          this.updateMainProduct(doc);
          this.updatePageTitle(doc);
          this.updateMetaTags(doc);
          this.dispatchProductChangeEvent(productUrl);

          this.classList.remove('connected-products--loading');
          this.scrollToProduct();

        } catch (error) {
          console.error('Connected Products: Error fetching product page', error);
          this.classList.remove('connected-products--loading');
          window.location.href = productUrl;
        }
      }

      updateMainProduct(doc) {
        const currentMainProduct = document.querySelector(`#MainProduct-${this.sectionId}`);
        const newMainProduct = doc.querySelector(`[id^="MainProduct-"]`);
        let currentMainProductSection = null;

        if (currentMainProduct && newMainProduct) {
          currentMainProductSection = currentMainProduct.closest('.shopify-section');

          currentMainProduct.innerHTML = newMainProduct.innerHTML;
          this.reinitializeComponents(currentMainProduct);
        }

        if (!currentMainProductSection) {
          console.error("Connected Products: Could not find main product section wrapper.");
          return;
        }

        const productInfoSection = document.querySelector('.product-info-section');
        const newProductInfoSection = doc.querySelector('.product-info-section');
        if (productInfoSection && newProductInfoSection) {
          productInfoSection.innerHTML = newProductInfoSection.innerHTML;
          this.reinitializeComponents(productInfoSection);
        }

        const materialFeatureTemplate = document.querySelector('section[id^="material-feature-template--"][id*="__novicell_material_feature_"]');
        const newMaterialFeatureTemplate = doc.querySelector('section[id^="material-feature-template--"][id*="__novicell_material_feature_"]');
        if (materialFeatureTemplate && newMaterialFeatureTemplate) {
          materialFeatureTemplate.innerHTML = newMaterialFeatureTemplate.innerHTML;
          this.reinitializeComponents(materialFeatureTemplate);
        }

        if (window.CSS && CSS.supports && CSS.supports('selector(:has(*))')) {
          const videoSelector = '.shopify-section:has(.video-section)';
          this.updateDynamicSection(doc, videoSelector, currentMainProductSection);
        }

        const flowboxSelector = '.shopify-section[id^="shopify-section-template--"][id*="__novicell_flowbox_"]';
        this.updateDynamicSection(doc, flowboxSelector, currentMainProductSection);

        this.updateRelatedProducts(doc);
        this.updateProductJson(doc);
      }

      /**
       * Determines the correct insertion point by looking at the section's previous sibling in the new doc
       */
      findInsertionAnchor(doc, newSection, currentMainProductSection) {
        const prevSibling = newSection.previousElementSibling;

        // Case A: No previous sibling -> It's the first section
        if (!prevSibling) {
          return { reference: currentMainProductSection.parentNode, method: 'prepend' };
        }

        // Case B: Previous sibling is the Main Product Section
        if (prevSibling.querySelector('[id^="MainProduct-"]')) {
          return { reference: currentMainProductSection, method: 'after' };
        }

        // Case C: Previous sibling is a standard section (Static or previously inserted)
        // We try to find this exact section ID in the live DOM
        if (prevSibling.id) {
          const existingSibling = document.getElementById(prevSibling.id);
          if (existingSibling) {
            return { reference: existingSibling, method: 'after' };
          }
        }

        // Case D: Fallback
        // If we can't find the specific sibling (e.g., ID mismatch), safe fallback to Main Product
        return { reference: currentMainProductSection, method: 'after' };
      }

      updateDynamicSection(doc, selector, mainProductSection) {
        const currentSection = document.querySelector(selector);
        const newSection = doc.querySelector(selector);
        let resultingSection = null;

        if (currentSection && newSection) {
          // REPLACE (Position is preserved automatically)
          currentSection.replaceWith(newSection);
          resultingSection = newSection;
        } else if (currentSection && !newSection) {
          // REMOVE
          currentSection.remove();
          resultingSection = null;
        } else if (!currentSection && newSection) {
          // INSERT (Need to calculate correct position)
          const anchor = this.findInsertionAnchor(doc, newSection, mainProductSection);

          if (anchor.method === 'prepend') {
            anchor.reference.prepend(newSection);
          } else {
            anchor.reference.after(newSection);
          }

          resultingSection = newSection;
        }

        if (resultingSection) {
          this.reinitializeComponents(resultingSection);
        }

        return resultingSection;
      }

      reinitializeComponents(container) {
        const variantSelects = container.querySelectorAll('variant-selects, variant-radios');
        variantSelects.forEach((element) => {
          if (element.connectedCallback) element.connectedCallback();
        });

        const productForms = container.querySelectorAll('product-form');
        productForms.forEach((form) => {
          if (form.connectedCallback) form.connectedCallback();
        });

        const productInfos = container.querySelectorAll('product-info');
        productInfos.forEach((info) => {
          if (info.connectedCallback && !info.__ncConnectedCallbackInvoked) {
            info.connectedCallback();
            info.__ncConnectedCallbackInvoked = true;
          }
        });
        const connectedProducts = container.querySelectorAll('connected-products');
        connectedProducts.forEach((cp) => {
          if (cp !== this && cp.connectedCallback && !cp.__ncConnectedCallbackInvoked) {
            cp.connectedCallback();
            cp.__ncConnectedCallbackInvoked = true;
          }
        });
        const quantityInputs = container.querySelectorAll('quantity-input');
        quantityInputs.forEach((input) => {
          if (input.connectedCallback && !input.__ncConnectedCallbackInvoked) {
            input.connectedCallback();
            input.__ncConnectedCallbackInvoked = true;
          }
        });
        const slideshows = container.querySelectorAll('slideshow-component, slider-component');
        slideshows.forEach((slideshow) => {
          if (slideshow.resetPages) {
            slideshow.resetPages();
          } else if (slideshow.connectedCallback && !slideshow.__ncConnectedCallbackInvoked) {
            slideshow.connectedCallback();
            slideshow.__ncConnectedCallbackInvoked = true;
          }
        });

        const mediaGalleries = container.querySelectorAll('[id^="MediaGallery-"]');
        if (mediaGalleries.length > 0) {
          window.dispatchEvent(new Event('resize'));
        }

        if (typeof window.initProductInformation === 'function') {
          window.initProductInformation();
        }

        if (typeof window.initMaterialFeature === 'function') {
          window.initMaterialFeature();
        }
      }

      updatePageTitle(doc) {
        const newTitle = doc.querySelector('title');
        if (newTitle) {
          document.title = newTitle.textContent;
        }
      }

      updateMetaTags(doc) {
        const currentCanonical = document.querySelector('link[rel="canonical"]');
        const newCanonical = doc.querySelector('link[rel="canonical"]');
        if (currentCanonical && newCanonical) {
          currentCanonical.href = newCanonical.href;
        }

        const ogTags = ['og:title', 'og:description', 'og:image', 'og:url'];
        ogTags.forEach((tag) => {
          const currentTag = document.querySelector(`meta[property="${tag}"]`);
          const newTag = doc.querySelector(`meta[property="${tag}"]`);
          if (currentTag && newTag) {
            currentTag.content = newTag.content;
          }
        });
      }

      updateRelatedProducts(doc) {
        const currentRelated = document.querySelector('.related-products, [class*="related-products"]');
        const newRelated = doc.querySelector('.related-products, [class*="related-products"]');

        if (currentRelated && newRelated) {
          currentRelated.innerHTML = newRelated.innerHTML;
        }
      }

      updateProductJson(doc) {
        const currentJson = document.querySelector('[data-product-json]');
        const newJson = doc.querySelector('[data-product-json]');

        if (currentJson && newJson) {
          currentJson.innerHTML = newJson.innerHTML;
        }
      }

      dispatchProductChangeEvent(productUrl) {
        const event = new CustomEvent('connected-product:changed', {
          bubbles: true,
          detail: {
            productUrl: productUrl,
            sectionId: this.sectionId,
          },
        });
        this.dispatchEvent(event);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'connected-products' });
        }
      }

      scrollToProduct() {
        const mainProduct = document.querySelector(`#MainProduct-${this.sectionId}`);
        if (mainProduct) {
          mainProduct.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      disconnectedCallback() {
        this.inputs.forEach((input) => {
          input.removeEventListener('change', this.boundOnProductChange);
        });
      }
    }
  );
}